import React from "react";
import { useTranslation } from "react-i18next";
import { AllDataAccount } from "../../../generated/types";
import { DataTableColumn, DataTableRow } from "../../common/DataTable";
import { FavoriteButton, FavoriteHeader } from "../../common/FavoriteButton";
import { UserTypeBadge } from "./UserTypeBadge";

export type UserColumnKey =
  | "favorite"
  | "type"
  | "company"
  | "lastName"
  | "firstName"
  | "email";

/** Les colonnes triables sont celles qui ont une valeur de tri. */
export const USER_SORT_VALUES: Partial<
  Record<UserColumnKey, (user: AllDataAccount) => string>
> = {
  type: (u) => String(u.account.type),
  company: (u) => u.company.company_name,
  lastName: (u) => u.account.last_name,
  firstName: (u) => u.account.first_name,
  email: (u) => u.account.email,
};

export const useUserColumns = (): DataTableColumn<UserColumnKey>[] => {
  const { t } = useTranslation();
  const columns: DataTableColumn<UserColumnKey>[] = [
    {
      key: "favorite",
      header: <FavoriteHeader />,
      className: "w-8 flex items-center justify-center",
    },
    { key: "type", header: t("account-type"), className: "w-1/5 lg:w-2/12" },
    { key: "company", header: t("company"), className: "w-1/5 lg:w-3/12" },
    {
      key: "lastName",
      header: t("account-last-name"),
      className: "w-1/5 lg:w-2/12",
    },
    {
      key: "firstName",
      header: t("account-first-name"),
      className: "w-1/5 lg:w-2/12",
    },
    {
      key: "email",
      header: t("account-email"),
      className: "hidden lg:block w-1/6 truncate",
    },
  ];
  return columns.map((column) => ({
    ...column,
    sortable: column.key in USER_SORT_VALUES,
  }));
};

type UserRowProps = {
  user: AllDataAccount;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  onClick: () => void;
  selected?: boolean;
};

export const UserRow = ({
  user,
  isFavorite,
  onToggleFavorite,
  onClick,
  selected = false,
}: UserRowProps) => {
  const columns = useUserColumns();

  return (
    <DataTableRow
      columns={columns}
      onClick={onClick}
      selected={selected}
      cells={{
        favorite: (
          <FavoriteButton isFavorite={isFavorite} onToggle={onToggleFavorite} />
        ),
        type: <UserTypeBadge type={user.account.type} />,
        company: user.company.company_name,
        lastName: user.account.last_name,
        firstName: user.account.first_name,
        email: <span title={user.account.email}>{user.account.email}</span>,
      }}
    />
  );
};
